import { Box, Fade, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { CustomerRevenueReportItem } from '@vy/types';
import { isEmptyArray } from '@vy/utils';
import { useCallback } from 'react';
import { REPORT_CARD_SX, REPORT_CHART_COLORS } from '../../../reports-components/chart-theme';
import { ReportChartLegend, type ReportChartLegendItem } from '../../../reports-components/ReportChartLegend';
import { reportToggleGroupSx } from '../../../reports-components/report-toolbar-styles';
import { CustomerRevenueReportBreakdownChart } from './CustomerRevenueReportBreakdownChart';
import {
  useCustomerRevenueReportChartData,
  type ChartPeriodMode,
} from './use-customer-revenue-report-chart-data';

type CustomerRevenueReportChartProps = {
  reportItems: CustomerRevenueReportItem[];
  fromDate?: Date | null;
  toDate?: Date | null;
  periodMode: ChartPeriodMode;
  onPeriodModeChange: (mode: ChartPeriodMode) => void;
};

export const CustomerRevenueReportChart: React.FC<CustomerRevenueReportChartProps> = ({
  reportItems,
  fromDate,
  toDate,
  periodMode,
  onPeriodModeChange,
}) => {
  const data = useCustomerRevenueReportChartData({ reportItems, fromDate, toDate, periodMode });
  const isEmpty = isEmptyArray(data);

  const handlePeriodChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, nextMode: ChartPeriodMode | null) => {
      if (nextMode) onPeriodModeChange(nextMode);
    },
    [onPeriodModeChange],
  );

  return (
    <Box sx={{ px: '16px', pb: '20px' }}>
      <Fade in timeout={400}>
        <Box sx={REPORT_CARD_SX}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2
            }}>
            <ToggleButtonGroup
              value={periodMode}
              exclusive
              onChange={handlePeriodChange}
              size="small"
              sx={reportToggleGroupSx()}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="quarterly">Quarterly</ToggleButton>
              <ToggleButton value="annual">Annual</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {isEmpty ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 150
              }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                No revenue data for the selected filters
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CustomerRevenueReportBreakdownChart data={data} />
              </Box>
              <ReportChartLegend items={BREAKDOWN_LEGEND} orientation="vertical" />
            </Box>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

const BREAKDOWN_LEGEND: ReportChartLegendItem[] = [
  { label: 'Recognized', color: REPORT_CHART_COLORS.recognized },
  { label: 'Deferred', color: REPORT_CHART_COLORS.deferred },
  { label: 'Unbilled', color: REPORT_CHART_COLORS.unbilled },
];
